import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Close as CloseIcon,
  Info as InfoIcon,
  Map as MapIcon,
  Search as SearchIcon,
  Feedback as FeedbackIcon,
  Download as DownloadIcon,
  Email as EmailIcon
} from '@mui/icons-material';

const Header = ({ title }) => {
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const handleOpen = () => setInstructionsOpen(true);
  const handleClose = () => setInstructionsOpen(false);

  return (
    <>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ flexDirection: 'column', py: 1 }}>
          <Typography variant="h5" component="div" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            Reference Condition Monitoring Program
          </Typography>
          <Typography variant="subtitle1" component="div" sx={{ textAlign: 'center', opacity: 0.9 }}>
            Reference and flow status dashboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<InfoIcon />}
              onClick={handleOpen}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { 
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                },
                textTransform: 'none'
              }}
            >
              Instructions
            </Button>
            <Button
              variant="outlined"
              size="small"
              href="https://ftp.sccwrp.org/pub/download/PROJECTS/SMC/Documents/status-definition.pdf"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255,255,255,0.5)',
                '&:hover': { 
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                },
                textTransform: 'none'
              }}
            >
              Status Definitions
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Instructions Modal */}
      <Dialog 
        open={instructionsOpen} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InfoIcon />
            <Typography variant="h6">Instructions</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'inherit' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Welcome to the Reference and flow status dashboard
          </Typography>
          <Typography variant="body1" paragraph>
            This application allows you to explore California’s bioassessment stations, 
            view their reference and flow status classifications, and provide feedback on station data.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            How to Use This Application
          </Typography>

          <List dense>
            <ListItem>
              <ListItemIcon><MapIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Explore the Map" 
                secondary="Click on station markers to view detailed information. Markers are color-coded by the status."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><SearchIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Filter Stations" 
                secondary="Use the sidebar to filter stations by County or Regional Water Board. You can also search for specific stations by ID."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><FeedbackIcon color="primary" /></ListItemIcon>
              <ListItemText 
                primary="Provide Feedback" 
                secondary="Click 'Provide Feedback' on any station to submit corrections or comments about reference status, flow status, or delineation issues. Email verification is required for first-time submissions."
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Contact Information
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <EmailIcon color="primary" />
            <Typography variant="body1">
              For questions or support, please contact Raphael Mazor: <strong>raphaelm@sccwrp.org</strong>
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Southern California Coastal Water Research Project (SCCWRP)
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleClose}>
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;
